import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import API from "../../services/api.ts";
import { toast } from "react-toastify";
import Navbar from "../navbar/Navbar";
import "./Datasets.css";
import "../shared/starfield.css";

const DatasetsPage = () => {
    const navigate = useNavigate();
    const [datasets, setDatasets] = useState([]);
    const [projects, setProjects] = useState([]);
    const [patients, setPatients] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchDatasets = useCallback(async () => {
        try {
            setLoading(true);
            const response = await API.get("/datasets/");
            setDatasets(response.data);
        } catch (err) {
            console.error("Error fetching datasets:", err);
            toast.error("Failed to fetch datasets.");
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchProjects = useCallback(async () => {
        try {
            const response = await API.get("/projects/");
            setProjects(response.data);
        } catch (err) {
            console.error("Error fetching projects:", err);
        }
    }, []);

    // Patients are only fetched to power the summary counts on this page.
    // Full CRUD lives on /Patients.
    const fetchPatients = useCallback(async () => {
        try {
            const response = await API.get("/data_manager/patients/");
            setPatients(response.data);
        } catch (err) {
            console.error("Error fetching patients:", err);
        }
    }, []);

    useEffect(() => {
        fetchDatasets();
        fetchProjects();
        fetchPatients();
    }, [fetchDatasets, fetchProjects, fetchPatients]);

    const getProjectName = (projectId) => {
        if (!projectId) return null;
        const project = projects.find(p => p.id === projectId);
        return project ? project.name : "Unknown Project";
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString();
    };

    return (
        <div className="datasets-page">
            {/* Starfield Background */}
            <div className="starfield-background"></div>

            <Navbar />

            <div className="container py-4">
                {/* Page Header */}
                <div className="datasets-header-card">
                    <div className="datasets-header-content">
                        <div className="datasets-info">
                            <h1 className="datasets-title">Datasets</h1>
                            <p className="datasets-description">
                                Manage your medical imaging datasets and assign them to projects
                            </p>
                        </div>
                        <div className="d-flex gap-2">
                            <button
                                className="btn-create-dataset"
                                onClick={() => navigate("/CreateDataset")}
                            >
                                <i className="fa fa-plus me-2"></i>
                                New Dataset
                            </button>
                        </div>
                    </div>
                </div>

                {/* Stats Row */}
                <div className="datasets-stats">
                    <div className="stat-card">
                        <div className="stat-icon">
                            <i className="fa fa-database"></i>
                        </div>
                        <div className="stat-value">{datasets.length}</div>
                        <div className="stat-label">Total Datasets</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon">
                            <i className="fa fa-users"></i>
                        </div>
                        <div className="stat-value">{patients.length}</div>
                        <div className="stat-label">Total Patients</div>
                        <button
                            className="btn btn-sm btn-outline-primary mt-2"
                            onClick={() => navigate("/Patients")}
                        >
                            <i className="fa fa-arrow-right me-1"></i>
                            See All Patients
                        </button>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon">
                            <i className="fa fa-folder-open"></i>
                        </div>
                        <div className="stat-value">
                            {datasets.filter(d => d.project).length}
                        </div>
                        <div className="stat-label">Assigned to Projects</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon">
                            <i className="fa fa-images"></i>
                        </div>
                        <div className="stat-value">
                            {patients.reduce((sum, p) => sum + (p.data_count?.total || 0), 0)}
                        </div>
                        <div className="stat-label">Total Data Items</div>
                    </div>
                </div>

                {/* Datasets Table */}
                <div className="datasets-card">
                    <div className="datasets-card-header">
                        <div className="datasets-card-title">
                            <i className="fa fa-database datasets-card-icon"></i>
                            <div>
                                <h3>All Datasets</h3>
                                <p className="datasets-card-subtitle">View and manage your datasets</p>
                            </div>
                        </div>
                    </div>
                    <div className="datasets-card-content">
                        {loading ? (
                            <div className="loading-state">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : datasets.length === 0 ? (
                            <div className="empty-state">
                                <i className="fa fa-database"></i>
                                <p>No datasets yet</p>
                                <button
                                    className="btn btn-primary mt-3"
                                    onClick={() => navigate("/CreateDataset")}
                                >
                                    Create your first dataset
                                </button>
                            </div>
                        ) : (
                            <table className="datasets-table">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Description</th>
                                        <th>Patients</th>
                                        <th>Project</th>
                                        <th>Created</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {datasets.map((dataset) => (
                                        <tr
                                            key={dataset.id}
                                            className="dataset-row"
                                            style={{ cursor: "pointer" }}
                                            onClick={() => navigate(`/Datasets/${dataset.id}`)}
                                        >
                                            <td>
                                                <div>
                                                    <strong>{dataset.name}</strong>
                                                    {!dataset.is_owner && dataset.created_by_user && (
                                                        <small className="d-block text-muted">
                                                            by {dataset.created_by_user.first_name || dataset.created_by_user.username}
                                                        </small>
                                                    )}
                                                    {!dataset.is_owner && dataset.is_added && !dataset.is_available && (
                                                        <small className="d-block text-danger">
                                                            <i className="fa fa-exclamation-triangle me-1"></i>
                                                            Made private by owner
                                                        </small>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="description-cell">
                                                {dataset.description || "-"}
                                            </td>
                                            <td>
                                                <span className="patient-count-badge" title="Patients in dataset">
                                                    <i className="fa fa-users me-1"></i>
                                                    {dataset.patient_count || 0}
                                                </span>
                                            </td>
                                            <td>
                                                {dataset.project ? (
                                                    <span className="project-badge">
                                                        {getProjectName(dataset.project)}
                                                    </span>
                                                ) : (
                                                    <span className="no-project">Unassigned</span>
                                                )}
                                            </td>
                                            <td>{formatDate(dataset.created_at)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>

            </div>

        </div>
    );
};

export default DatasetsPage;
